/**
 * Canonical encoding and detached signatures.
 *
 * Specification: ../../test/crypto.spec.ts. Master plan §3 K8, §3 K9, §2 D1.
 *
 * Four callers sign or hash structured data — the audit chain, config bundles,
 * entitlement tokens and the venue hub's device roster. They share ONE encoder,
 * because the moment two of them disagree about how an object becomes bytes the
 * signature is unverifiable, and the symptom is every device in the estate
 * failing at once with nothing in the log but "bad signature".
 *
 * Ed25519 throughout: 32-byte keys, 64-byte signatures, no curve, hash or
 * padding parameters to get wrong.
 */

/** Refusal to encode. Always names the path, because "invalid value" is unfixable. */
export class CanonicalError extends Error {
  readonly path: string;

  constructor(path: string, problem: string) {
    super(`cannot canonicalise ${path}: ${problem}`);
    this.name = "CanonicalError";
    this.path = path;
  }
}

/**
 * The same tag the durable stores and the audit log encode with.
 *
 * Imported rather than restated: `assertNotReserved` below refuses this exact
 * key in data so that `{"$gfBigInt":"5"}` and the bigint `5n` cannot produce
 * identical bytes. If the signer and the encoders ever disagreed about the
 * constant, that collision guard would silently stop guarding the thing it
 * names.
 */
import { BIGINT_TAG } from "../json";

/**
 * Deterministic JSON: keys sorted by code unit at every level, no whitespace.
 *
 * Stricter than `JSON.stringify`, and every refusal below is a silent
 * corruption it would otherwise commit. See the specification for what each one
 * costs when it is allowed through.
 */
export function canonicalJson(value: unknown): string {
  return encode(value, "$", new Set());
}

/** The UTF-8 bytes of `canonicalJson`. What is actually signed and hashed. */
export function canonicalBytes(value: unknown): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(canonicalJson(value));
}

function encode(value: unknown, path: string, seen: Set<object>): string {
  if (value === null) return "null";

  switch (typeof value) {
    case "boolean":
      return value ? "true" : "false";

    case "bigint":
      return `{${JSON.stringify(BIGINT_TAG)}:${JSON.stringify(value.toString())}}`;

    case "number":
      // `JSON.stringify(NaN)` is the string "null": a corrupted number becomes
      // a legitimate-looking absent one, inside the structure whose entire
      // purpose is detecting corruption.
      if (!Number.isFinite(value)) {
        throw new CanonicalError(path, `${String(value)} has no canonical form`);
      }
      // An auditor recomputing this hash in another language needs the decimal
      // expansion to agree exactly, and shortest-round-trip float formatting is
      // not guaranteed to. Money is bigint by D1, so nothing legitimate needs it.
      if (!Number.isInteger(value)) {
        throw new CanonicalError(
          path,
          `${value} is not an integer — use a bigint for money, or a string`,
        );
      }
      return JSON.stringify(value);

    case "string":
      // Deliberately NOT unicode-normalised: an audit entry records what it was
      // given, and normalising would make the chain verify while quietly
      // disagreeing with the source record.
      return JSON.stringify(value);

    case "undefined":
      // `JSON.stringify` drops the key. Then `{a:1}` and `{a:1,b:undefined}`
      // sign identically, and "did this field exist?" — exactly what an auditor
      // asks — becomes unanswerable.
      throw new CanonicalError(path, "undefined is not representable; use null to record an absence");

    case "function":
    case "symbol":
      throw new CanonicalError(path, `a ${typeof value} cannot be signed`);

    case "object":
      return encodeObject(value as object, path, seen);

    default:
      throw new CanonicalError(path, `unsupported type ${typeof value}`);
  }
}

function encodeObject(value: object, path: string, seen: Set<object>): string {
  // Reported as a refusal with the offending path rather than as the
  // RangeError a naive recursion would raise several thousand frames later.
  if (seen.has(value)) throw new CanonicalError(path, "circular reference");

  if (value instanceof Date) {
    // A walker built on Object.entries sees no own enumerable keys on a Date
    // and emits `{}` — silently turning every timestamp in every signed payload
    // into the same two characters.
    const ms = value.getTime();
    if (Number.isNaN(ms)) throw new CanonicalError(path, "an invalid Date has no canonical form");
    return JSON.stringify(value.toISOString());
  }

  const next = new Set(seen).add(value);

  if (Array.isArray(value)) {
    // Order preserved: in an array the order IS the value.
    return `[${value.map((item, i) => encode(item, `${path}[${i}]`, next)).join(",")}]`;
  }

  assertNotReserved(value, path);

  const keys = Object.keys(value).sort(compareCodeUnits);
  const members = keys.map((key) => {
    const encoded = encode((value as Record<string, unknown>)[key], `${path}.${key}`, next);
    return `${JSON.stringify(key)}:${encoded}`;
  });
  return `{${members.join(",")}}`;
}

/**
 * Refuse data that would collide with the bigint tag.
 *
 * Without this the literal `{"$gfBigInt":"5"}` and the bigint `5n` encode to
 * identical bytes — two different values sharing one hash, which is precisely
 * the property a chain must not have.
 */
function assertNotReserved(value: object, path: string): void {
  if (BIGINT_TAG in value) {
    throw new CanonicalError(path, `"${BIGINT_TAG}" is a reserved key and cannot appear in data`);
  }
}

/**
 * Sort by UTF-16 code unit, never by locale.
 *
 * `localeCompare` in cs-CZ orders "Á" next to "a" and "Z" last; in en-US it
 * does not. A signature produced under one collation would not verify under
 * another, so the estate would work or fail according to each machine's locale.
 */
const compareCodeUnits = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// --- Signing ---------------------------------------------------------------

export interface SigningKeyPair {
  readonly privateKey: CryptoKey;
  readonly publicKey: CryptoKey;
}

const ED25519 = { name: "Ed25519" } as const;

export async function generateSigningKeyPair(): Promise<SigningKeyPair> {
  const pair = (await crypto.subtle.generateKey(ED25519, true, ["sign", "verify"])) as CryptoKeyPair;
  return { privateKey: pair.privateKey, publicKey: pair.publicKey };
}

/** Detached signature over exactly these bytes, base64url. */
export async function sign(message: Uint8Array, privateKey: CryptoKey): Promise<string> {
  const signature = await crypto.subtle.sign(ED25519, privateKey, intoBuffer(message));
  return base64url(signature);
}

/**
 * Verify a detached signature.
 *
 * Returns false on malformed input rather than throwing: a verifier that raises
 * on garbage turns "an attacker sent nonsense" into a 500 and a pager, when the
 * correct answer is a clean no.
 */
export async function verify(
  message: Uint8Array,
  signature: string,
  publicKey: CryptoKey,
): Promise<boolean> {
  let bytes: Uint8Array<ArrayBuffer>;
  try {
    bytes = fromBase64url(signature);
  } catch {
    return false;
  }

  // No length check here on purpose. WebCrypto already returns false for a
  // signature that is not 64 bytes, and the catch below would turn a throw into
  // false anyway — so an explicit check would be a line with no observable
  // behaviour, which is a line no test can prove works.
  try {
    return await crypto.subtle.verify(ED25519, publicKey, intoBuffer(bytes), intoBuffer(message));
  } catch {
    return false;
  }
}

/** The raw 32-byte public key, base64url — what a device pins. */
export async function exportVerifyingKey(publicKey: CryptoKey): Promise<string> {
  return base64url(await crypto.subtle.exportKey("raw", publicKey));
}

export async function importVerifyingKey(raw: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", intoBuffer(fromBase64url(raw)), ED25519, true, ["verify"]);
}

/**
 * BASE64URL WITHOUT `Buffer`, AND THAT IS THE WHOLE POINT.
 *
 * This used `Buffer.from(...).toString("base64url")`, and every test passed,
 * because Bun provides `Buffer` as a global exactly as Node does. A BROWSER
 * DOES NOT — so in a real Chrome the till could not verify a signed config
 * bundle (no floor plan, no cash increment, no venue clock, and a console
 * warning saying "keeping the cached copy" when there was none) and could not
 * compute a PIN verifier either, so nobody could sign in.
 *
 * The bundle gate could not catch it: it looks for externalized `node:`
 * specifiers, and `Buffer` is not an import — it is a bare global the bundler
 * leaves alone and the browser has never heard of. It was found by opening the
 * built app in Chrome, which nothing had done before.
 *
 * `btoa`/`atob` are in every browser, Bun and Node. Everything here goes
 * through this one pair; `@gf/kernel/staff-auth` had its own copy and now does
 * not, because a second implementation is a second thing to get wrong in a
 * second environment.
 */
export function toBase64Url(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);

  // Chunked: `String.fromCharCode(...view)` overflows the argument limit on a
  // large input, and the inputs here are small only until somebody signs a
  // config bundle.
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < view.length; i += CHUNK) {
    binary += String.fromCharCode(...view.subarray(i, i + CHUNK));
  }

  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  // CHECKED BEFORE DECODING rather than by round-tripping afterwards. `atob` is
  // lenient in its own ways, and a pinned key that decodes to a prefix of
  // itself fails every signature check for a reason nobody can find.
  if (!/^[A-Za-z0-9_-]*$/.test(value)) throw new Error("not valid base64url");
  // Unpadded is the canonical form here. A length of 1 mod 4 cannot encode any
  // whole number of bytes.
  if (value.length % 4 === 1) throw new Error("not valid base64url");

  const padded = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(
    value.length + ((4 - (value.length % 4)) % 4),
    "=",
  );

  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

const base64url = toBase64Url;
const fromBase64url = fromBase64Url;

/**
 * WebCrypto's BufferSource wants a plain ArrayBuffer backing, and a Uint8Array
 * from Buffer is a view onto a shared pool — passing it directly signs whatever
 * else happens to be in that pool's window.
 */
const intoBuffer = (bytes: Uint8Array): ArrayBuffer =>
  bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
